import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { appsAPI } from '../api/appsAPI';
import { Logo_black_iconOnly } from '../../../components/Logo/Logo';
import './CreatorsListPage.css';

/**
 * Directory page showcasing all Pathbuddy content creators.
 * Paths: /creators (landing page context) and /apps-store/creators (app store context)
 */
const CreatorsListPage = ({ fromLanding = false }) => {
  const navigate = useNavigate();
  const [creators, setCreators] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchCreators = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await appsAPI.getCreatorsPublic();
        if (!cancelled && res && res.success) {
          setCreators(res.creators || []);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load creators directory.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchCreators();
    return () => { cancelled = true; };
  }, []);

  const backUrl = fromLanding ? '/' : '/apps-store';
  
  const handleCreatorClick = (slug) => {
    if (fromLanding) {
      navigate(`/creator/${slug}`);
    } else {
      navigate(`/apps-store/creator/${slug}`);
    }
    window.scrollTo(0, 0);
  };

  // Filter creators based on search query
  const filteredCreators = creators.filter(c => {
    const fullName = `${c.first_name} ${c.last_name}`.toLowerCase();
    const slug = (c.creator_slug || '').toLowerCase();
    const subject = (c.primary_subject || '').toLowerCase();
    const query = searchQuery.toLowerCase();
    return fullName.includes(query) || slug.includes(query) || subject.includes(query);
  });

  return (
    <div className="creators-directory-page app-store-view">
      <header className="creator-page-header app-store-header app-store-header-full">
        <div className="creator-page-header-inner">
          <div 
            className="creator-page-logo-wrap" 
            onClick={() => navigate(backUrl)} 
            role="button" 
            tabIndex={0} 
            onKeyDown={(e) => e.key === 'Enter' && navigate(backUrl)} 
            aria-label="Home"
          >
            <Logo_black_iconOnly />
          </div>
          <h1 className="creator-page-header-title">Creator Directory</h1>
        </div>
      </header>

      <main className="creators-directory-main">
        <div className="creators-directory-content">
          <div className="directory-header-section">
            <h1 className="directory-title">Ivori Creators</h1>
            <p className="directory-subtitle">
              Browse interactive case studies, worksheets, and guides built by our student creators and domain experts.
            </p>
            
            <div className="directory-search-wrapper">
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder="Search creators by name, nickname, or subject..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="directory-search-input"
              />
            </div>
          </div>

          {loading && <p className="directory-message">Loading creators...</p>}
          {error && <p className="directory-message directory-error">{error}</p>}

          {!loading && !error && filteredCreators.length === 0 && (
            <p className="directory-message">No creators found matching your search query.</p>
          )}

          {!loading && !error && filteredCreators.length > 0 && (
            <div className="creators-grid">
              {filteredCreators.map((creator) => {
                const displayName = creator.first_name 
                  ? `${creator.first_name} ${creator.last_name}` 
                  : creator.creator_slug;
                
                const initials = creator.first_name 
                  ? `${creator.first_name[0]}${creator.last_name ? creator.last_name[0] : ''}` 
                  : creator.creator_slug[0];

                return (
                  <div
                    key={creator.creator_slug}
                    role="button"
                    tabIndex={0}
                    className="creator-directory-card"
                    onClick={() => handleCreatorClick(creator.creator_slug)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreatorClick(creator.creator_slug)}
                  >
                    <div className="card-avatar-wrap">
                      <div className="card-avatar">{initials.toUpperCase()}</div>
                      <span className="card-app-count">
                        {creator.app_count} {creator.app_count === 1 ? 'App' : 'Apps'}
                      </span>
                    </div>
                    
                    <div className="card-info">
                      <h3 className="card-display-name">{displayName}</h3>
                      <p className="card-headline">
                        {creator.professional_summary || 'Case Study Author'}
                      </p>
                      
                      {creator.bio && (
                        <p className="card-bio-excerpt">{creator.bio}</p>
                      )}
                      
                      <div className="card-footer-badges">
                        {creator.primary_subject && (
                          <span className="card-badge subject-badge">
                            🎓 {creator.primary_subject.toUpperCase()}
                          </span>
                        )}
                        {(creator.city || creator.country) && (
                          <span className="card-badge location-badge">
                            📍 {creator.city || creator.country}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CreatorsListPage;
